import { compressSnapshot, decompressSnapshot } from '@/lib/compression';
import { handleBackgroundError } from '@/lib/error-handler';
import type { Session, SessionEvent, SessionId, SessionSnapshot } from '@/schemas';
import { createEventId } from '@/schemas';
import { SessionSchema, SessionSnapshotSchema } from '@/schemas';
import { replaySessionEvents } from '@/stores/event-replay';
import { db } from './client';
import { getSessionEvents } from './session-events';

/**
 * Configuration for snapshot creation
 */
const SNAPSHOT_INTERVAL = 100; // Create snapshot every N events
const SNAPSHOT_RETENTION_COUNT = 5; // Keep last N snapshots per session
const SNAPSHOT_RETENTION_INTERVAL = 100; // Keep snapshots spaced by at least N events

/**
 * Create a snapshot of a session at a specific event count
 * @param sessionId - Session ID to snapshot
 * @param eventCount - Number of events to include in snapshot
 * @param compress - Whether to compress the snapshot (default: true)
 * @returns Snapshot ID
 */
export async function createSessionSnapshot(
  sessionId: SessionId,
  eventCount: number,
  compress: boolean = true,
): Promise<string> {
  // Load all events up to eventCount
  const allEvents = await getSessionEvents(sessionId);
  const eventsForSnapshot = allEvents.slice(0, eventCount);

  if (eventsForSnapshot.length === 0) {
    throw new Error('Cannot create snapshot: no events found');
  }

  // Replay events to get full session state
  const session = replaySessionEvents(eventsForSnapshot);

  // Validate session matches schema
  const validatedSession = SessionSchema.parse(session);

  // Serialize session to JSON
  const snapshotJson = JSON.stringify(validatedSession);

  // Compress if requested and supported
  let snapshotData: string;
  let isCompressed = false;
  if (compress) {
    try {
      snapshotData = await compressSnapshot(snapshotJson);
      isCompressed = true;
    } catch (error) {
      // Compression failed (API not available or error), store uncompressed
      handleBackgroundError(error, { operation: 'compressSnapshot', sessionId }, 'warn');
      snapshotData = snapshotJson;
      isCompressed = false;
    }
  } else {
    snapshotData = snapshotJson;
  }

  // Store snapshot
  const snapshotId = createEventId();
  const snapshot: SessionSnapshot = {
    id: snapshotId,
    sessionId,
    snapshot: snapshotData,
    eventCount,
    createdAt: Date.now(),
    compressed: isCompressed,
  };

  await db.sessionSnapshots.add(SessionSnapshotSchema.parse(snapshot));

  return snapshotId;
}

/**
 * Get the latest snapshot for a session
 * @param sessionId - Session ID
 * @returns Latest snapshot or undefined if none exists
 */
export async function getLatestSnapshot(
  sessionId: SessionId,
): Promise<SessionSnapshot | undefined> {
  const snapshots = await db.sessionSnapshots
    .where('sessionId')
    .equals(sessionId)
    .sortBy('eventCount');

  if (snapshots.length === 0) {
    return undefined;
  }

  // Return the snapshot with the highest eventCount (most recent in terms of session state)
  // This is more reliable than createdAt since snapshots can be created in the same millisecond
  const latest = snapshots.at(-1);
  return SessionSnapshotSchema.parse(latest);
}

/**
 * Get events that occurred after a snapshot's event count
 * @param sessionId - Session ID
 * @param snapshotEventCount - Event count of the snapshot
 * @returns Events after the snapshot
 */
export async function getEventsAfterSnapshot(
  sessionId: SessionId,
  snapshotEventCount: number,
): Promise<SessionEvent[]> {
  const allEvents = await getSessionEvents(sessionId);
  // Return events after the snapshot's event count
  return allEvents.slice(snapshotEventCount);
}

/**
 * Deserialize a snapshot to a Session object
 * @param snapshot - Snapshot to deserialize
 * @returns Deserialized Session
 */
export async function deserializeSnapshot(snapshot: SessionSnapshot): Promise<Session> {
  let snapshotJson: string;

  if (snapshot.compressed) {
    try {
      snapshotJson = await decompressSnapshot(snapshot.snapshot);
    } catch (error) {
      handleBackgroundError(error, { operation: 'decompressSnapshot', snapshotId: snapshot.id });
      throw new Error('Failed to decompress snapshot');
    }
  } else {
    snapshotJson = snapshot.snapshot;
  }

  try {
    const parsed = JSON.parse(snapshotJson) as unknown;
    const session = SessionSchema.parse(parsed);
    return session;
  } catch (error) {
    handleBackgroundError(error, { operation: 'parseSnapshot', snapshotId: snapshot.id });
    throw new Error('Failed to parse snapshot: invalid JSON or schema mismatch');
  }
}

/**
 * Check if a snapshot should be created for a session
 * @param sessionId - Session ID
 * @param currentEventCount - Current number of events
 * @returns True if snapshot should be created
 */
export async function shouldCreateSnapshot(
  sessionId: SessionId,
  currentEventCount: number,
): Promise<boolean> {
  // Only create snapshots at intervals (e.g., every 100 events)
  if (currentEventCount % SNAPSHOT_INTERVAL !== 0) {
    return false;
  }

  // Check if snapshot already exists for this event count (deduplication)
  const existing = await db.sessionSnapshots
    .where('sessionId')
    .equals(sessionId)
    .and((s) => s.eventCount === currentEventCount)
    .first();

  return existing === undefined;
}

/**
 * Create a snapshot if needed (non-blocking)
 * @param sessionId - Session ID
 * @param eventCount - Current event count
 */
export async function createSnapshotIfNeeded(
  sessionId: SessionId,
  eventCount: number,
): Promise<void> {
  const shouldCreate = await shouldCreateSnapshot(sessionId, eventCount);
  if (!shouldCreate) {
    return;
  }

  // Create snapshot in background (non-blocking)
  // Use setTimeout to defer to next event loop
  setTimeout(() => {
    void (async () => {
      try {
        await createSessionSnapshot(sessionId, eventCount);
        // Cleanup old snapshots after creating new one
        // Apply both retention policies: keep last N, and keep 1 per interval
        await cleanupOldSnapshots(sessionId);
        await cleanupSnapshotsByEventCount(sessionId);
      } catch (error) {
        handleBackgroundError(error, { operation: 'createSnapshot', sessionId }, 'warn');
        // Don't throw - snapshot creation is non-critical
      }
    })();
  }, 0);
}

/**
 * Cleanup old snapshots, keeping only the last N snapshots per session
 * @param sessionId - Session ID
 * @param keepCount - Number of snapshots to keep (default: SNAPSHOT_RETENTION_COUNT)
 */
export async function cleanupOldSnapshots(
  sessionId: SessionId,
  keepCount: number = SNAPSHOT_RETENTION_COUNT,
): Promise<void> {
  const snapshots = await db.sessionSnapshots
    .where('sessionId')
    .equals(sessionId)
    .sortBy('createdAt');

  if (snapshots.length <= keepCount) {
    return;
  }

  // Delete oldest snapshots, keeping the last N
  const toDelete = snapshots.slice(0, snapshots.length - keepCount);
  const idsToDelete = toDelete.map((s) => s.id);
  await db.sessionSnapshots.bulkDelete(idsToDelete);
}

/**
 * Cleanup snapshots that are too close together (keep 1 per N events)
 * @param sessionId - Session ID
 * @param minEventInterval - Minimum event interval between snapshots (default: SNAPSHOT_RETENTION_INTERVAL)
 */
export async function cleanupSnapshotsByEventCount(
  sessionId: SessionId,
  minEventInterval: number = SNAPSHOT_RETENTION_INTERVAL,
): Promise<void> {
  const snapshots = await db.sessionSnapshots
    .where('sessionId')
    .equals(sessionId)
    .sortBy('eventCount');

  if (snapshots.length <= 1) {
    return;
  }

  // Keep first snapshot, then keep subsequent snapshots only if they're spaced by minEventInterval
  const toKeep: string[] = [snapshots[0].id];
  let lastKeptEventCount = snapshots[0].eventCount;

  for (let i = 1; i < snapshots.length; i++) {
    const snapshot = snapshots[i];
    if (snapshot.eventCount - lastKeptEventCount >= minEventInterval) {
      toKeep.push(snapshot.id);
      lastKeptEventCount = snapshot.eventCount;
    }
  }

  // Delete snapshots not in toKeep list
  const toDelete = snapshots.filter((s) => !toKeep.includes(s.id));
  const idsToDelete = toDelete.map((s) => s.id);
  if (idsToDelete.length > 0) {
    await db.sessionSnapshots.bulkDelete(idsToDelete);
  }
}

/**
 * Delete all snapshots for a session
 * @param sessionId - Session ID
 */
export async function deleteSessionSnapshots(sessionId: SessionId): Promise<void> {
  await db.sessionSnapshots.where('sessionId').equals(sessionId).delete();
}
